import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Gift, CheckCircle2, XCircle, KeyRound, BadgeCheck, Send, Clock, BellRing } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../i18n";
import { CraftLogo, LangSwitcher, CraftFooter } from "../components/Shared";

const fmt = (n) => (n || 0).toLocaleString("ru-RU");

export default function CertificatePage() {
  const { code } = useParams();
  const { t } = useLang();
  const [cert, setCert] = useState(null);
  const [err, setErr] = useState(false);
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState(false);
  const [justRedeemed, setJustRedeemed] = useState(false);
  const [tgState, setTgState] = useState("idle");
  const tgPollRef = useRef(null);

  useEffect(() => {
    api.get(`/public/certificate/${code}`).then((r) => setCert(r.data)).catch(() => setErr(true));
    return () => { clearInterval(tgPollRef.current); };
  }, [code]);

  const redeem = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/public/certificate/${code}/redeem`, { secret });
      setCert(data);
      setJustRedeemed(true);
      toast.success(t("cert_redeemed_ok"));
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d === "invalid_secret" ? t("cert_wrong_secret") : d === "already_redeemed" ? t("cert_already") : d === "expired" ? t("cert_expired") : d === "not_activated" ? t("cert_not_activated") : t("server_error"));
    }
    setBusy(false);
  };

  const tgConfirm = async () => {
    setTgState("sending");
    try {
      const { data } = await api.post(`/public/certificate/${code}/tg-confirm`);
      setTgState("waiting");
      tgPollRef.current = setInterval(async () => {
        try {
          const { data: st } = await api.get(`/public/certificate/${code}/tg-confirm/${data.confirm_id}`);
          if (st.status === "confirmed") {
            clearInterval(tgPollRef.current);
            setTgState("done");
            setJustRedeemed(true);
            const { data: c2 } = await api.get(`/public/certificate/${code}`);
            setCert(c2);
            toast.success(t("cert_redeemed_ok"));
          } else if (st.status === "declined") {
            clearInterval(tgPollRef.current);
            setTgState("idle");
            toast.error(t("cert_tg_declined"));
          } else if (["expired", "failed"].includes(st.status)) {
            clearInterval(tgPollRef.current);
            setTgState("idle");
          }
        } catch (e) { /* poll retry */ }
      }, 2500);
    } catch (e) {
      setTgState("idle");
      const d = e.response?.data?.detail;
      toast.error(d === "no_cashiers" ? t("cert_no_cashiers") : d === "already_redeemed" ? t("cert_already") : d === "not_activated" ? t("cert_not_activated") : t("server_error"));
    }
  };

  const active = cert?.status === "active" && !cert?.expired;
  return (
    <div className="min-h-screen bg-[#FBF3E4]">
      <div className="max-w-[430px] mx-auto min-h-screen flex flex-col">
        <div className="flex items-center justify-between px-5 pt-5 pb-3"><CraftLogo /><LangSwitcher /></div>
        <div className="px-5 flex-1 space-y-4">
          {err && <div className="craft-card p-6 text-center font-bold" data-testid="cert-not-found">{t("cert_not_found")}</div>}
          {cert && (
            <>
              <div className="craft-card overflow-hidden" data-testid="cert-public-card">
                <div className="h-2 bg-[#FF0000]" />
                <div className="p-6 text-center space-y-3">
                  <div className="w-16 h-16 mx-auto rounded-full bg-[#FEBC10]/20 flex items-center justify-center"><Gift size={32} className="text-[#FEBC10]" /></div>
                  <h1 className="font-display font-black text-xl uppercase leading-tight">{t("cert_title")}</h1>
                  <div className="font-display font-black text-4xl text-[#FF0000]" data-testid="cert-amount">{fmt(cert.amount)} <span className="text-xl">{t("sum")}</span></div>
                  <div className="font-display font-black tracking-[0.15em] text-gray-500">{cert.code}</div>
                  {cert.expires ? (
                    <div className="text-xs font-bold text-gray-500 flex items-center justify-center gap-1" data-testid="cert-validity">
                      <Clock size={13} /> {t("cert_expires")}: {cert.expires}
                    </div>
                  ) : cert.activation_days ? (
                    <div className="text-xs font-bold text-gray-500 flex items-center justify-center gap-1 flex-wrap px-2" data-testid="cert-validity">
                      <Clock size={13} /> {t("cert_term")}: {cert.activation_days} {t("cert_term_after")}
                    </div>
                  ) : null}
                  {active ? (
                    <span data-testid="cert-status-badge" className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-green-100 text-green-700 text-sm font-bold"><CheckCircle2 size={16} /> {t("cert_valid")}</span>
                  ) : cert.status === "redeemed" ? (
                    <span data-testid="cert-status-badge" className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-gray-200 text-gray-500 text-sm font-bold"><BadgeCheck size={16} /> {t("cert_redeemed")}{cert.redeemed_at ? ` · ${new Date(cert.redeemed_at).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}` : ""}</span>
                  ) : cert.expired && cert.status === "active" ? (
                    <span data-testid="cert-status-badge" className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-orange-100 text-orange-600 text-sm font-bold"><Clock size={16} /> {t("cert_expired")}</span>
                  ) : (
                    <span data-testid="cert-status-badge" className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-red-100 text-red-600 text-sm font-bold"><XCircle size={16} /> {t("cert_void")}</span>
                  )}
                </div>
              </div>

              {active && !cert.activated && (
                <div className="craft-card p-4 flex items-center gap-3 bg-amber-50" data-testid="cert-activate-pending">
                  <Clock size={20} className="text-amber-500 shrink-0" />
                  <div className="text-xs font-semibold text-amber-700">{t("cert_activate_admin_hint")}</div>
                </div>
              )}
              {cert.activated && (
                <div className="craft-card p-4 flex items-center gap-3 bg-green-50" data-testid="cert-activated-badge">
                  <BellRing size={22} className="text-green-600 shrink-0" />
                  <div className="text-sm font-bold text-green-700">{t("cert_activated")}{cert.holder_name ? ` · ${cert.holder_name}` : ""}</div>
                </div>
              )}

              {justRedeemed && (
                <div className="craft-card p-5 text-center space-y-1 bg-green-50" data-testid="cert-redeemed-banner">
                  <CheckCircle2 size={28} className="mx-auto text-green-600" />
                  <div className="font-display font-black">{t("cert_redeemed_ok")}</div>
                </div>
              )}

              {active && (
                <div className="craft-card p-5 space-y-3" data-testid="cert-redeem-form">
                  <p className="text-xs text-gray-500 font-semibold text-center">{t("cert_show_cashier")}</p>
                  {tgState === "waiting" ? (
                    <div className="flex items-center justify-center gap-2 py-3 text-gray-600 font-semibold text-sm" data-testid="cert-tg-waiting">
                      <span className="w-2 h-2 rounded-full bg-[#229ED9] animate-pulse" /> {t("cert_tg_waiting")}
                    </div>
                  ) : (
                    <>
                      <button data-testid="cert-tg-confirm-btn" onClick={tgConfirm} disabled={tgState === "sending"}
                        className="w-full min-h-[52px] rounded-2xl bg-[#229ED9] hover:bg-[#1e8fc4] text-white font-bold text-base flex items-center justify-center gap-2.5 shadow-[0_4px_14px_rgba(34,158,217,0.4)] transition-transform active:scale-[0.97] disabled:opacity-60">
                        <span className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center"><Send size={15} /></span>
                        {t("cert_tg_confirm_btn")}
                      </button>
                      <p className="text-[11px] text-gray-400 font-semibold text-center">{t("cert_tg_confirm_hint")}</p>
                    </>
                  )}
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-px bg-gray-200" />
                    <span className="text-[11px] font-black uppercase text-gray-400">{t("cert_or")}</span>
                    <div className="flex-1 h-px bg-gray-200" />
                  </div>
                  <label className="text-sm font-bold text-gray-700 flex items-center gap-1.5"><KeyRound size={15} /> {t("cert_secret_label")}</label>
                  <input data-testid="cert-secret-input" className="craft-input text-center font-black tracking-widest" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder="••••••" />
                  <button data-testid="cert-redeem-btn" className="craft-btn craft-btn-red" disabled={busy || secret.trim().length < 4} onClick={redeem}>{t("cert_redeem_btn")}</button>
                </div>
              )}
            </>
          )}
          {!cert && !err && <div className="flex justify-center py-16"><div className="w-10 h-10 border-4 border-[#FEBC10] border-t-transparent rounded-full animate-spin" /></div>}
        </div>
        <CraftFooter />
      </div>
    </div>
  );
}
